import { InsightsOutlined, TimelineOutlined, TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Skeleton, styled, Tooltip } from '@mui/material';
import { ReactNode, useState } from 'react';
import { BodyText } from '@/components/BodyText';
import {
  ActivityCountKey,
  ActivityMetrics,
  ActivityPeriodDays,
  ActivityPeriods,
  useGetTenantActivityMetrics,
} from '@/hooks/api/tenants/useGetTenantActivityMetrics';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useTenants } from '@/services/tenants/useTenants';
import { COUNTING_NOTE, INTERNAL_USERS_NOTE, METRIC_DESCRIPTIONS, trendDescription } from './activityDescriptions';
import { ACTIVITY_SERIES } from './activitySeries';
import { ActivitySparkline } from './ActivitySparkline';
import { describeTrend, TrendDirection } from './activityTrend';
import { DashboardTile } from './DashboardTile';
import { FeatureUsageList } from './FeatureUsageList';
import { TileEmptyState } from './TileEmptyState';

const PERIOD_OPTIONS: { value: ActivityPeriodDays; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const TREND_ICONS: Record<TrendDirection, ReactNode> = {
  up: <TrendingUp fontSize='inherit' />,
  down: <TrendingDown fontSize='inherit' />,
  flat: <TrendingFlat fontSize='inherit' />,
};

const SEARCH_TYPE_LABELS: Record<keyof ActivityMetrics['searchesByType'], string> = {
  text: 'Text',
  visual: 'Visual',
  geometric: 'Geometric',
  part: 'Part',
  composite: 'Composite',
  metadata: 'Metadata',
};

const REPORT_TYPE_LABELS: Record<keyof ActivityMetrics['reportsByType'], string> = {
  DUPLICATION: 'Duplication',
  SIMPLIFICATION: 'Simplification',
  CUSTOM: 'Custom',
};

const CardsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '12px',
});

const MetricCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '12px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
}));

const HeadlineRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
});

const TrendRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.875rem',
});

const Detail = styled(BodyText)({
  minHeight: '1.25rem',
});

function describeBreakdown<Key extends string>(counts: Record<Key, number>, labels: Record<Key, string>): string {
  return (Object.entries(counts) as [Key, number][])
    .filter(([, count]) => count > 0)
    .map(([key, count]) => `${labels[key]} ${count.toLocaleString()}`)
    .join(' · ');
}

function describeActiveUsers({ activeUsers, internalActiveUsers }: ActivityMetrics): string {
  if (activeUsers === 0) return '';
  return `${internalActiveUsers.toLocaleString()} Physna · ${(activeUsers - internalActiveUsers).toLocaleString()} customer`;
}

const detailFor = (key: ActivityCountKey, metrics: ActivityMetrics): string => {
  if (key === 'activeUsers') return describeActiveUsers(metrics);
  if (key === 'searches') return describeBreakdown(metrics.searchesByType, SEARCH_TYPE_LABELS);
  if (key === 'reports') return describeBreakdown(metrics.reportsByType, REPORT_TYPE_LABELS);
  return '';
};

const ActivityMetricCard = ({
  metric,
  label,
  periods,
  days,
}: {
  metric: ActivityCountKey;
  label: string;
  periods: ActivityPeriods;
  days: ActivityPeriodDays;
}) => {
  const value = periods.current[metric];
  const trend = describeTrend(value, periods.previous[metric]);
  const detail = detailFor(metric, periods.current);
  const testId = `activity-${metric}`;

  return (
    <MetricCard data-testid={`${testId}-card`}>
      <BodyText color='text.secondary'>{label}</BodyText>
      <HeadlineRow>
        <Tooltip title={METRIC_DESCRIPTIONS[metric]} placement='top-start' arrow>
          <BodyText fontSize='1.5rem' fontWeight={600} data-testid={testId}>
            {value.toLocaleString()}
          </BodyText>
        </Tooltip>
        <Tooltip title={trendDescription(days)} placement='top-start' arrow>
          <TrendRow
            color={trend.direction === 'up' ? 'success.main' : 'text.secondary'}
            data-testid={`${testId}-trend`}
          >
            {TREND_ICONS[trend.direction]}
            <BodyText fontSize='0.75rem' color='inherit'>
              {trend.label}
            </BodyText>
          </TrendRow>
        </Tooltip>
      </HeadlineRow>
      {metric === 'activeUsers' && detail ? (
        <Tooltip title={INTERNAL_USERS_NOTE} placement='top-start' arrow>
          <Detail fontSize='0.75rem' color='text.secondary' data-testid={`${testId}-detail`}>
            {detail}
          </Detail>
        </Tooltip>
      ) : (
        <Detail fontSize='0.75rem' color='text.secondary' data-testid={`${testId}-detail`}>
          {detail}
        </Detail>
      )}
      <ActivitySparkline metric={metric} label={label} daily={periods.current.daily} />
    </MetricCard>
  );
};

const ActivitySection = ({ periods, days }: { periods: ActivityPeriods; days: ActivityPeriodDays }) => (
  <>
    <BodyText fontSize='0.75rem' color='text.secondary'>
      {`Change is against the previous ${days} days. Active users are counted, never named. ${COUNTING_NOTE} Hover a number for what one count means.`}
    </BodyText>
    <CardsGrid>
      {ACTIVITY_SERIES.map(series => (
        <ActivityMetricCard key={series.key} metric={series.key} label={series.label} periods={periods} days={days} />
      ))}
    </CardsGrid>
    <FeatureUsageList current={periods.current.featureUsage} previous={periods.previous.featureUsage} days={days} />
  </>
);

const isQuiet = (metrics: ActivityMetrics) =>
  ACTIVITY_SERIES.filter(series => series.key !== 'activeUsers').every(series => metrics[series.key] === 0) &&
  Object.values(metrics.featureUsage).every(count => count === 0);

const hasNoActivity = ({ current, previous }: ActivityPeriods) => isQuiet(current) && isQuiet(previous);

const NoActivity = ({ days }: { days: ActivityPeriodDays }) => {
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();

  return (
    <TileEmptyState
      testId='no-activity'
      graphic={<InsightsOutlined />}
      title={`No usage in the last ${days} days`}
      description='Searches, compares, downloads, uploads and reports by your team show here.'
      action={permissions.canManageUsers ? { label: 'Invite teammates', to: getPath('users') } : undefined}
    />
  );
};

export const ActivityTile = () => {
  const [days, setDays] = useState<ActivityPeriodDays>(7);
  const { data: periods, isLoading, isError } = useGetTenantActivityMetrics(days);

  const handlePeriodChange = (event: SelectChangeEvent<ActivityPeriodDays>) => {
    setDays(Number(event.target.value) as ActivityPeriodDays);
  };

  return (
    <DashboardTile
      id='activity'
      title='Activity'
      icon={<TimelineOutlined />}
      action={
        <FormControl size='small' variant='standard'>
          <Select<ActivityPeriodDays>
            value={days}
            onChange={handlePeriodChange}
            data-testid='activity-period-select'
            inputProps={{ 'aria-label': 'Activity period' }}
          >
            {PERIOD_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value} data-testid={`activity-period-${option.value}`}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
    >
      {isError && <BodyText color='text.secondary'>Failed to load activity.</BodyText>}
      {!isError && isLoading && <Skeleton variant='rounded' height={340} />}
      {!isError && !isLoading && periods && hasNoActivity(periods) && <NoActivity days={days} />}
      {!isError && !isLoading && periods && !hasNoActivity(periods) && (
        <ActivitySection periods={periods} days={days} />
      )}
    </DashboardTile>
  );
};
